<template>
  <div v-if="visible" class="notification__container">
    <v-card class="notification__card" :elevation="elevation">
      <div class="notification__header">
        <v-icon dark size="20" class="mr-2">mdi-bell-ring</v-icon>
        <span class="notification__title">{{ resolvedTitle }}</span>
        <v-spacer></v-spacer>
        <v-chip x-small dark class="notification__badge" :ripple="false">
          {{ totalCount }}
        </v-chip>
        <v-btn icon x-small dark class="ml-1" @click="collapsed = !collapsed">
          <v-icon>{{ collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </div>

      <v-expand-transition>
        <div v-show="!collapsed">
          <div v-if="items.length === 0" class="notification__empty">
            {{ $t('operation.noActiveNotifications') }}
          </div>
          <template v-else>
            <div class="notification__list" :style="{ maxHeight: `${maxListHeight}px` }">
              <v-slide-y-transition group tag="div">
                <div
                  v-for="notif in items"
                  :key="`N-${notif.notificacao.id_notificacao}`"
                  class="notification__item"
                  :style="{ borderLeftColor: notif.color || defaultColor }"
                  @click="onItemClick(notif)"
                >
                  <v-icon :color="notif.color || defaultColor" size="22" class="notification__item-icon">
                    {{ iconeTipoNotificacao(notif.notificacao.id_tipo_notificacao) }}
                  </v-icon>
                  <div class="notification__item-content">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <div class="notification__item-title" v-on="on">
                          {{ notif.notificacao.descricao_tipo_notificacao }}
                        </div>
                      </template>
                      <span>{{ notif.notificacao.descricao_tipo_notificacao }}</span>
                    </v-tooltip>
                    <div class="notification__item-subtitle">
                      <strong>{{ notif.notificacao.cod_veiculo || notif.notificacao.nome_equipamento }}</strong>
                      <span class="notification__item-time">
                        {{ horaNotificacao(notif.notificacao) }}
                      </span>
                    </div>
                    <div
                      v-if="notif.notificacao.detalhe"
                      class="notification__item-detalhe"
                    >{{ notif.notificacao.detalhe }}</div>
                  </div>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small color="primary" v-on="on" @click.stop="$emit('tratar', notif.notificacao)">
                        <v-icon size="20">mdi-check-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('operation.treatNotification') }}</span>
                  </v-tooltip>
                </div>
              </v-slide-y-transition>
            </div>
            <div v-if="items.length > 0" class="notification__more">
              <v-btn x-small text color="primary" @click="$emit('ver-mais')">
                {{ $t('operation.notificationsTitle') }}
              </v-btn>
            </div>
          </template>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "NotificacoesOperacaoCard",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mapEnabled: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: null,
    },
    defaultColor: {
      type: String,
      default: "#F44336",
    },
    maxListHeight: {
      type: Number,
      default: 230,
    },
    elevation: {
      type: [Number, String],
      default: 8,
    },
  },
  data() {
    return {
      collapsed: false,
    };
  },
  computed: {
    resolvedTitle() {
      if (this.title != null && String(this.title).trim() !== "") {
        return this.title;
      }
      return this.$t("operation.activeNotifications");
    },
  },
  methods: {
    onItemClick(notif) {
      this.$emit("item-click", notif.notificacao, notif.color);
    },
    horaNotificacao(notif) {
      const s = notif.dt_notificacao_extenso;
      if (!s || typeof s !== "string" || s.length < 11) {
        return "";
      }
      return s.substring(11);
    },
    iconeTipoNotificacao(idTipo) {
      const map = {
        2: "mdi-map-marker-radius",
        3: "mdi-map-marker-radius-outline",
        4: "mdi-shield-home",
        5: "mdi-shield-home-outline",
        6: "mdi-map-marker-path",
        7: "mdi-speedometer",
        8: "mdi-crosshairs-off",
        9: "mdi-credit-card-off",
        12: "mdi-crosshairs-gps",
        13: "mdi-credit-card-check",
        14: "mdi-bus-alert",
        15: "mdi-account-alert",
        16: "mdi-cash-multiple",
        17: "mdi-clipboard-alert",
        18: "mdi-clipboard-multiple",
        19: "mdi-bus-stop",
        20: "mdi-bus-multiple",
        21: "mdi-crosshairs-question",
        22: "mdi-alert-octagon",
      };
      return map[idTipo] || "mdi-alert-circle";
    },
  },
};
</script>

<style scoped>
.notification__container {
  position: fixed;
  bottom: 16px;
  right: 16px;
  /* Acima dos panes do Leaflet (~400–700) e dos controles (~1000), abaixo dos drawers/dialogs do app */
  z-index: 2500;
  width: 400px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}

.notification__card {
  pointer-events: auto;
  border-radius: 12px !important;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.98) !important;
}

.notification__header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(90deg, #c62828, #ef5350);
  color: white;
  user-select: none;
}

.notification__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.notification__badge {
  background-color: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  font-weight: bold;
  height: 18px !important;
  padding: 0 8px !important;
}

.notification__empty {
  padding: 16px 12px;
  font-size: 13px;
  color: #757575;
  text-align: center;
}

.notification__list {
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px;
}

.notification__list::-webkit-scrollbar {
  width: 6px;
}

.notification__list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 3px;
}

.notification__list::-webkit-scrollbar-track {
  background: transparent;
}

.notification__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  margin: 3px 2px;
  border-radius: 8px;
  border-left: 4px solid #f44336;
  background: #fafafa;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.notification__item:hover {
  background: #f0f0f0;
  transform: translateX(-2px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.notification__item-icon {
  flex-shrink: 0;
}

.notification__item-content {
  flex: 1;
  min-width: 0;
}

.notification__item-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #212121;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification__item-subtitle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  font-size: 11.5px;
  color: #616161;
}

.notification__item-time {
  font-feature-settings: "tnum";
  color: #757575;
}

.notification__item-detalhe {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #424242;
  white-space: pre-line;
  max-height: 72px;
  overflow-y: auto;
}

.notification__more {
  text-align: center;
  padding: 4px 0;
  border-top: 1px solid #eee;
}
</style>
